import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "text-sm text-(--colors-text-body-subtle) mb-4" };
const _hoisted_3 = { class: "space-y-4" };
const _hoisted_4 = {
    key: 0,
    role: "status",
    class: "rounded-md border border-(--colors-border-warning-subtle) bg-(--colors-background-warning) px-3 py-2 text-sm text-(--colors-text-warning)",
    "data-testid": "collecte-form-duplicate-campaign-notice"
};
const _hoisted_5 = {
    key: 1,
    role: "alert",
    class: "text-sm text-(--colors-text-error)",
    "data-testid": "collecte-form-duplicate-error"
};
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Modal, TextInput, Button } from "@eudonet/opal";
import { useDonationFormStore } from "../../stores/useDonationFormStore";
import { useTakenCampaigns } from "../composables/useTakenCampaigns";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { useAppToast } from "@/shared/composables/useAppToast";
// Backend errorCode -> i18n key for the duplicate failures the manager can recover from this form
// (R70). The expected recoverable failure is a 409 Form.PublicSlug.Conflict (the new slug is already
// taken); the slug/title 422 codes are FE-pre-validated safety nets kept mapped so a command-validator
// 422 still renders an actionable message, and Form.NotFound covers the source being deleted meanwhile.
// Unknown codes fall back to the generic saveFailed key. The codes are the wire contract
// (DonationFormErrorCodes); duplication introduces none of its own, and the one code added
// is shared with the create and campaign-setter paths.
const SLUG_MAX_LENGTH = 128;
const TITLE_MAX_LENGTH = 255;
// Seed the candidate title + slug each time the modal opens so a previous edit never leaks into a new
// duplicate, and the fields always reflect the CURRENTLY chosen source row. The title carries the
// localized copy marker (R76 — the FE supplies "(copy)"/"(copie)"/"(kopie)", never a backend constant),
// clamped to the 255-char bound; the slug appends a neutral `-copy` suffix, clamped to the 128-char bound
// and stripped of any trailing hyphen (mirrors FormCreateModal's slugify tail) so clamping a near-max
// source can never seed a slug that ends in a hyphen and fails SLUG_PATTERN. Both stay fully editable.
export default /*@__PURE__*/ _defineComponent({
    __name: 'FormDuplicateModal',
    props: /*@__PURE__*/ _mergeModels({
        sourceId: {},
        sourceSlug: {},
        sourceTitle: {},
        sourceCampaignId: {},
        sourceStatus: {}
    }, {
        "modelValue": { type: Boolean, ...{ required: true } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["duplicated"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const DUPLICATE_ERROR_KEYS = {
            "Form.PublicSlug.Conflict": "collecte.forms.duplicate.errors.slugConflict",
            "Form.PublicSlug.Invalid": "collecte.forms.duplicate.errors.slugFormat",
            "Form.PublicSlug.TooLong": "collecte.forms.duplicate.errors.slugTooLong",
            "Form.PublicSlug.Required": "collecte.forms.duplicate.errors.slugRequired",
            "Form.Title.Required": "collecte.forms.duplicate.errors.titleRequired",
            "Form.Title.TooLong": "collecte.forms.duplicate.errors.titleTooLong",
            "Form.NotFound": "collecte.forms.duplicate.errors.notFound",
            // 409 — a campaign carries at most ONE live donation form. This modal sends no
            // campaignId, so the copy INHERITS the source's, and an inherited-but-taken campaign is degraded
            // to a campaign-less copy (201) rather than refused — the `campaignWillBeLost` notice above is
            // what covers that path. The code is still reachable here, on the narrow TOCTOU window the
            // handler documents: a concurrent request takes the campaign between the resolution and the
            // flush, and the handler cannot re-resolve on a closed EntityManager, so it answers 409 even for
            // an inherited campaign. Mapping it turns that into "retry", which is exactly the right advice.
            "Form.Campaign.AlreadyHasForm": "collecte.forms.duplicate.errors.campaignAlreadyHasForm",
        };
        const props = __props;
        /**
         * The tenant's campaigns already carried by a live form, re-primed on every open (see the `watch`
         * below). NO `ownCampaignId` exemption, unlike the two pickers: that option spares the campaign the
         * EDITED form itself holds, which here is exactly the fact the second branch of
         * {@link campaignWillBeLost} needs to see.
         */
        const { load: loadTakenCampaigns, isTaken: isCampaignTaken } = useTakenCampaigns();
        /**
         * Whether this duplication will produce a campaign-less copy.
         *
         * The backend resolves the copy's campaign as "inherit the source's, unless that campaign is
         * already carried by a live form, in which case start campaign-less" — `existsByCampaign` counts
         * every form on that campaign whose status is not `Archived`. The predicate mirrors it in two
         * halves, and BOTH are needed because they cover disjoint carriers:
         *
         *  1. A source that is itself live (Draft / Published / Paused) IS that carrier, so its own campaign
         *     is taken BY IT. Certain, from the row alone, with no lookup and no staleness.
         *  2. An ARCHIVED source released the campaign, so the copy inherits it legitimately — retire last
         *     year's form, duplicate it, keep the campaign. Unless a live SUCCESSOR has taken the campaign
         *     since, which is the second duplication of that very retire-then-restart flow: the copy comes
         *     back campaign-less and, until this branch existed, nothing on screen said so. Only the
         *     tenant-wide taken set can see that carrier, since it is neither the source nor on this row.
         *
         * The taken set is a LOWER BOUND (`useTakenCampaigns`: a truncated or failed sweep flags nothing),
         * so branch 2 can only ADD warnings that are right — never suppress branch 1, never invent a
         * carrier. Its one over-warning window is a live form archived between the sweep and the submit, in
         * which case the copy quietly KEEPS a campaign the manager was told it might lose — strictly milder
         * than the campaign-less copy with no notice that this replaces.
         */
        const campaignWillBeLost = computed(() => props.sourceCampaignId !== null &&
            (props.sourceStatus !== "Archived" || isCampaignTaken(props.sourceCampaignId)));
        const open = _useModel(__props, "modelValue");
        // `duplicated` carries the new form id so the parent navigates to the wizard (mirrors `created`).
        const emit = __emit;
        const { t } = useI18n();
        const store = useDonationFormStore();
        const { toastSuccess } = useAppToast();
        // Names the dialog for assistive tech (`aria-labelledby` -> the `<h2>` above).
        const titleId = useId();
        const title = ref("");
        const publicSlug = ref("");
        const titleError = ref(null);
        const slugError = ref(null);
        const submitError = ref(null);
        // Lower-case alphanumeric words separated by single hyphens (mirrors the backend slug rule / create).
        const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
        // Mirrors the backend bounds (DonationForm slug max 128, title max 255).
        watch(open, (isOpen) => {
            if (isOpen) {
                title.value = `${props.sourceTitle} ${t("collecte.forms.duplicate.copySuffix")}`
                    .trim()
                    .slice(0, TITLE_MAX_LENGTH);
                publicSlug.value = `${props.sourceSlug}-copy`.slice(0, SLUG_MAX_LENGTH).replace(/-+$/, "");
                titleError.value = null;
                slugError.value = null;
                submitError.value = null;
                // Re-collected on EVERY open, like FormCreateModal's: this modal stays mounted for the life of
                // the list view, so a sweep taken at mount would describe a tenant that has since moved on. Not
                // awaited and never rejecting — the dialog is fully usable while it runs, and the archived-source
                // branch of `campaignWillBeLost` simply repaints if it turns out to be true.
                void loadTakenCampaigns();
            }
        });
        function validate() {
            titleError.value = null;
            slugError.value = null;
            const trimmedTitle = title.value.trim();
            const trimmedSlug = publicSlug.value.trim();
            if (!trimmedTitle) {
                titleError.value = "collecte.forms.duplicate.errors.titleRequired";
            }
            else if (trimmedTitle.length > TITLE_MAX_LENGTH) {
                titleError.value = "collecte.forms.duplicate.errors.titleTooLong";
            }
            if (!trimmedSlug) {
                slugError.value = "collecte.forms.duplicate.errors.slugRequired";
            }
            else if (trimmedSlug.length > SLUG_MAX_LENGTH) {
                slugError.value = "collecte.forms.duplicate.errors.slugTooLong";
            }
            else if (!SLUG_PATTERN.test(trimmedSlug)) {
                slugError.value = "collecte.forms.duplicate.errors.slugFormat";
            }
            return !titleError.value && !slugError.value;
        }
        async function submit() {
            submitError.value = null;
            if (!validate()) {
                return;
            }
            try {
                const ack = await store.duplicateForm(props.sourceId, {
                    title: title.value.trim(),
                    publicSlug: publicSlug.value.trim(),
                });
                open.value = false;
                toastSuccess("collecte.forms.duplicate.success");
                // Hand the new id back to the parent so it navigates to the copy's first wizard section.
                emit("duplicated", ack.id);
            }
            catch (e) {
                // Map the backend errorCode to an actionable, localized message (R70) — a slug clash
                // (409 Form.PublicSlug.Conflict) is the duplicate flow's expected recoverable failure and must be
                // actionable, not a generic "save failed". resolveApiErrorMessage returns already-translated text
                // and falls back to saveFailed for unknown codes / network errors (R46 — user feedback, no logging).
                submitError.value = resolveApiErrorMessage(e, DUPLICATE_ERROR_KEYS, "collecte.forms.duplicate.errors.saveFailed");
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((open).value = $event)),
                size: "md",
                "aria-labelledby": _unref(titleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h2", {
                        id: _unref(titleId),
                        class: "text-xl font-bold text-(--colors-text-heading)",
                        "data-testid": "collecte-form-duplicate-heading"
                    }, _toDisplayString(_ctx.$t("collecte.forms.duplicate.title")), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        "data-testid": "collecte-form-duplicate-cancel",
                        onClick: _cache[2] || (_cache[2] = ($event) => (open.value = false))
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('collecte.forms.duplicate.submit'),
                        disabled: _unref(store).isSaving,
                        "data-testid": "collecte-form-duplicate-submit",
                        onClick: submit
                    }, null, 8 /* PROPS */, ["label", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("collecte.forms.duplicate.subtitle")), 1 /* TEXT */),
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(TextInput), {
                            modelValue: title.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((title).value = $event)),
                            label: _ctx.$t('collecte.forms.duplicate.titleLabel'),
                            placeholder: _ctx.$t('collecte.forms.duplicate.titlePlaceholder'),
                            error: titleError.value ? _ctx.$t(titleError.value) : undefined,
                            required: "",
                            "full-width": "",
                            "data-testid": "collecte-form-duplicate-title"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                        _createVNode(_unref(TextInput), {
                            modelValue: publicSlug.value,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((publicSlug).value = $event)),
                            label: _ctx.$t('collecte.forms.duplicate.slugLabel'),
                            placeholder: _ctx.$t('collecte.forms.duplicate.slugPlaceholder'),
                            error: slugError.value ? _ctx.$t(slugError.value) : undefined,
                            required: "",
                            "full-width": "",
                            "data-testid": "collecte-form-duplicate-slug"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                        (campaignWillBeLost.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_ctx.$t("collecte.forms.duplicate.campaignNotCopiedNotice")), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true),
                        (submitError.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(submitError.value), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby"]));
        };
    }
});
